<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'

const props = defineProps({
  leagueId: {
    type: [String, Number],
    required: true
  },
  teamId: {
    type: [String, Number],
    required: true
  },
  fixtureId: {
    type: [String, Number],
    required: true
  },
  players: {
    type: Array,
    required: false,
    default: () => []
  }
})

// Component state
const loading = ref(false)
const error = ref(null)
const projectionsSettings = ref(null)

// Helper function to get player name from player_id
const getPlayerName = (playerId) => {
  if (!props.players || !Array.isArray(props.players)) {
    return `Player ${playerId}`
  }
  
  const player = props.players.find(p => 
    p.id === playerId || 
    p.player_id === playerId || 
    p.player?.id === playerId ||
    p.player?.player_id === playerId
  )
  
  if (player) {
    return player.player_name || 
           player.name || 
           player.player?.player_name || 
           player.player?.name || 
           `Player ${playerId}`
  }
  
  return `Player ${playerId}`
}

// Group projections data by player
const groupedProjections = computed(() => {
  if (!projectionsSettings.value || !Array.isArray(projectionsSettings.value)) {
    return {}
  }
  
  return projectionsSettings.value.reduce((acc, setting) => {
    const playerId = setting.player_id
    if (!acc[playerId]) {
      acc[playerId] = {
        player_id: playerId,
        player_name: setting.player_name || setting.player?.player_name || setting.player?.name || `Player ${playerId}`,
        player: setting.player, // Include the full player object
        templates: {}
      }
    }
    
    acc[playerId].templates[setting.template_name] = setting
    return acc
  }, {})
})

// Get unique templates from all players
const uniqueTemplates = computed(() => {
  if (!projectionsSettings.value || !Array.isArray(projectionsSettings.value)) {
    return []
  }
  
  const templates = new Set()
  projectionsSettings.value.forEach(setting => {
    if (setting.template_name) {
      templates.add(setting.template_name)
    }
  })
  
  return Array.from(templates)
})

// Fetch projections settings on mount
const fetchProjectionsSettings = async () => {
  if (!props.leagueId || !props.teamId || !props.fixtureId) {
    error.value = 'League ID, Team ID, and Fixture ID are required'
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await axios.post('api/v2/projections-settings/projections-settings-player-minutes-settings', {
      league_id: props.leagueId,
      team_id: props.teamId,
      fixture_id: props.fixtureId,
      players: props.players
    })
    
    projectionsSettings.value = response.data
  } catch (err) {
    error.value = err.response?.data?.message || err.message || 'Failed to fetch projections settings'
    console.error('Error fetching projections settings:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchProjectionsSettings()
})
</script>

<template>
  <div class="space-y-4">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div class="flex items-center gap-3">
        <i class="fas fa-spinner fa-spin text-blue-500"></i>
        <span class="text-gray-600">Loading player minutes settings...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center gap-3">
        <i class="fas fa-exclamation-triangle text-red-500"></i>
        <div>
          <h4 class="text-red-800 font-medium">Error Loading Settings</h4>
          <p class="text-red-600 text-sm mt-1">{{ error }}</p>
        </div>
      </div>
      <button 
        @click="fetchProjectionsSettings" 
        class="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
      >
        <i class="fas fa-redo mr-2"></i>
        Retry
      </button>
    </div>

    <!-- Success State -->
    <div v-else-if="projectionsSettings" class="space-y-4">
      <div class="bg-green-50 border border-green-200 rounded-lg p-4">
        <div class="flex items-center gap-3">
          <i class="fas fa-check-circle text-green-500"></i>
          <div>
            <h4 class="text-green-800 font-medium">Player Minutes Settings Loaded</h4>
            <p class="text-green-600 text-sm mt-1">
              Found {{ Object.keys(groupedProjections).length }} players with {{ uniqueTemplates.length }} templates
            </p>
          </div>
        </div>
      </div>

      <!-- Projections Table -->
      <div class="bg-white rounded-lg border overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                <th v-for="template in uniqueTemplates" :key="template" 
                    class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ template.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(playerData, playerId) in groupedProjections" :key="playerId" class="hover:bg-gray-50">
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ playerData.player_name }}</div>
                  <div class="text-xs text-gray-500">ID: {{ playerData.player_id }}</div>
                </td>
                <td v-for="template in uniqueTemplates" :key="template" 
                    class="px-4 py-3 whitespace-nowrap text-center">
                  <div v-if="playerData.templates[template]" class="text-sm text-gray-900">
                    <div class="mb-1">
                      <span class="font-medium">{{ playerData.templates[template].per_game_minutes || 0 }}</span>
                      <span class="text-gray-400 ml-1">min</span>
                    </div>
                    <div class="text-xs text-gray-500">
                      <span class="font-medium">{{ playerData.templates[template].game_count || 0 }}</span>
                      <span class="text-gray-400 ml-1">games</span>
                    </div>
                  </div>
                  <div v-else class="text-sm text-gray-400">0</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Additional Details -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="bg-gray-50 rounded-lg p-4">
          <h5 class="font-medium text-gray-800 mb-3">Template Types</h5>
          <div class="space-y-1">
            <div v-for="template in [...new Set(projectionsSettings.map(s => s.template))]" :key="template" 
                 class="text-sm text-gray-600">
              <span class="font-medium">{{ template }}:</span> 
              {{ projectionsSettings.filter(s => s.template === template).length }} settings
            </div>
          </div>
        </div>
        
        <div class="bg-gray-50 rounded-lg p-4">
          <h5 class="font-medium text-gray-800 mb-3">League Distribution</h5>
          <div class="space-y-1">
            <div v-for="league in [...new Set(projectionsSettings.map(s => s.league_id))]" :key="league" 
                 class="text-sm text-gray-600">
              <span class="font-medium">League {{ league }}:</span> 
              {{ projectionsSettings.filter(s => s.league_id === league).length }} settings
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-8">
      <i class="fas fa-info-circle text-gray-400 text-4xl mb-4"></i>
      <p class="text-gray-600">No player minutes settings data available</p>
    </div>
  </div>
</template>

<style scoped>
/* Additional custom styles if needed */
</style>
